import React from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { TFunction } from "i18next";

import { ButtonClickCard } from "components";

import { IHearingFileDownloads } from "services/hearingFile";

import { convertType } from "utils/format";
import { mapTypeIcon } from "utils/fileDisplay";

import { AccordionContent } from "./styles";

type IsDownloadDisabled = (download: IHearingFileDownloads) => boolean;

const fileSize = (bytes: number) => {
  const total = (bytes / 1024).toFixed(2);
  if (Number(total) > 1000) {
    return (bytes / 1048576).toFixed(2) + " MB ";
  }
  return total + " KB";
};

const typeFile = (tipo: number, t: TFunction) => {
  if (tipo === 1) {
    return t("labels.video");
  }
  if (tipo === 2) {
    return t("labels.audio");
  }
  if (tipo === 3) {
    return t("labels.image");
  }
  if (tipo === 4) {
    return t("labels.document");
  }
  if (tipo === 5) {
    return t("downloads.attachedVideo");
  }
  if (tipo === 6) {
    return t("downloads.cutVideo");
  }
};

const buildGroupTitle = (item: IHearingFileDownloads, t: TFunction) => {
  return [t("recordings.singular"), item.name?.trim(), item.role?.trim()]
    .filter(Boolean)
    .join(" - ");
};

export const handleList = (
  listNotifications: Array<IHearingFileDownloads>,
  setItemClicked: React.Dispatch<
    React.SetStateAction<IHearingFileDownloads | null>
  >,
  tooltipMessage: string,
  t: TFunction,
  isDownloadDisabled?: IsDownloadDisabled,
) => {
  return listNotifications.map((item) => {
    const disabled = isDownloadDisabled?.(item) ?? false;

    return (
      <ButtonClickCard
        key={item.filename}
        title={item.filename}
        subTitle={`${convertType(item.type) || typeFile(item.type, t)}, ${fileSize(item.size)} `}
        tooltipMessagem={tooltipMessage}
        disabled={disabled}
        onClick={disabled ? undefined : () => setItemClicked(item)}
        typeButton="download"
        typeIcon={mapTypeIcon(item.type)}
      />
    );
  });
};

export const handleGroupedList = (
  listNotifications: Array<IHearingFileDownloads>,
  setItemClicked: React.Dispatch<
    React.SetStateAction<IHearingFileDownloads | null>
  >,
  tooltipMessage: string,
  t: TFunction,
  isDownloadDisabled?: IsDownloadDisabled,
) => {
  const groupedList = listNotifications.reduce(
    (acc, item) => {
      const groupKey = String(item.parentId ?? item.hearingFileId);

      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }

      acc[groupKey].push(item);
      return acc;
    },
    {} as Record<string, Array<IHearingFileDownloads>>,
  );

  return Object.entries(groupedList).map(([groupKey, items]) => {
    const firstItem = items[0];
    const groupTitle = buildGroupTitle(firstItem, t);
    return (
      <Accordion
        key={groupKey}
        disableGutters
        sx={{
          border: "1px solid #dfe3e8",
          borderRadius: "6px",
          boxShadow: "none",
          backgroundColor: "#fff",
          "&:before": { display: "none" },
          "&.Mui-expanded": {
            margin: 0,
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon fontSize="small" />}
          aria-controls={`downloads-group-${groupKey}-content`}
          id={`downloads-group-${groupKey}-header`}
          sx={{
            minHeight: "56px",
            padding: "0 14px",
            "&.Mui-expanded": {
              minHeight: "56px",
            },
            ".MuiAccordionSummary-content": {
              margin: "12px 0",
            },
            ".MuiAccordionSummary-content.Mui-expanded": {
              margin: "12px 0",
            },
          }}
        >
          {groupTitle}
        </AccordionSummary>
        <AccordionDetails sx={{ padding: "0 8px 8px 8px" }}>
          <AccordionContent>
            {items.map((item) => {
              const disabled = isDownloadDisabled?.(item) ?? false;

              return (
                <ButtonClickCard
                  key={item.hearingFileId}
                  title={item.filename}
                  subTitle={`${convertType(item.type) || typeFile(item.type, t)}, ${fileSize(item.size)} `}
                  tooltipMessagem={tooltipMessage}
                  disabled={disabled}
                  onClick={disabled ? undefined : () => setItemClicked(item)}
                  typeButton="download"
                  typeIcon={mapTypeIcon(item.type)}
                />
              );
            })}
          </AccordionContent>
        </AccordionDetails>
      </Accordion>
    );
  });
};
